import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LinkIcon from '@mui/icons-material/Link';
import { Alert, Backdrop, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, IconButton, InputLabel, MenuItem, Select, SelectChangeEvent, TextField, Tooltip, Checkbox, FormControlLabel, Switch } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { BusinessPartnerInfo, MapURLInformation } from "adoms-common-lib";
import axios from "axios";
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect } from "react";
import { APIConnector } from "../../connector/APIConnector";
import { SixSecondsDisplaySnackbar } from "../atoms/SixSecondsDisplaySnackbar";

type MapURLProps = {
    isCreateMapURLDialogOpen: boolean;
    handleClickCreateMapURLDialogClose: () => void;
    onMapURLSaved?: React.Dispatch<React.SetStateAction<boolean>>
    isEditMode: boolean;
    mapInfoToEdit?: MapURLInformation;
}

/**
 * マップURL作成・編集ダイアログ
 */
export const MapURLDialog: React.FC<MapURLProps> = (props) => {

    const [selectedBusinessPartnerId, setSelectedBusinessPartnerId] = React.useState<string>("");
    const [expirationDateTime, setExpirationDateTime] = React.useState<Dayjs | undefined>(dayjs().tz("Asia/Tokyo").add(1, 'day')); // Default to tomorrow
    const [remarks, setRemarks] = React.useState<string>(""); // Initialize as empty string
    const [showRoute, setShowRoute] = React.useState<boolean>(false);
    const [showIrregularFlights, setShowIrregularFlights] = React.useState<boolean>(false);
    const [allBusinessPartnerInfoList, setAllBusinessPartnerInfoList] = React.useState<BusinessPartnerInfo[]>([]);
    const [businessPartnerInfoError, setBusinessPartnerInfoError] = React.useState<string | undefined>(undefined);
    const [mapURLError, setMapURLError] = React.useState<string | undefined>(undefined);
    const [mapURL, setMapURL] = React.useState<string | undefined>(undefined);
    const [snackbarOpen, setSnackbarOpen] = React.useState(false);
    const [infoMessage, setInfoMessage] = React.useState<string>();
    const [isDisplayLoadingMark, setDisplayLoadingMark] = React.useState<boolean>(false);

    useEffect(() => {
        const fetchAllBusinessPartnerInfoList = async () => {
            let c: APIConnector = APIConnector.instance;
            try {
                const businessPartnerInfoList = await c.getBusinessPartnerInfoList();
                setAllBusinessPartnerInfoList(businessPartnerInfoList);
            } catch (error) {
                console.log(error);
                setBusinessPartnerInfoError("ビジネスパートナー情報を取得できませんでした。");
            }
        };
        fetchAllBusinessPartnerInfoList();
    }, []); // Removed props.isEditMode, props.mapInfoToEdit from dependency array to avoid issues with list fetching

    useEffect(() => {
        if (props.isEditMode && props.mapInfoToEdit) {
            setSelectedBusinessPartnerId(props.mapInfoToEdit.businessPartnerId);
            setExpirationDateTime(dayjs(props.mapInfoToEdit.expirationDateTime));
            setRemarks(props.mapInfoToEdit.remarks || "");
            setShowRoute(props.mapInfoToEdit.showRoute || false);
            setShowIrregularFlights(props.mapInfoToEdit.showIrregularFlights || false);
            setMapURL(undefined); // Clear previous URL if any
            setInfoMessage(undefined);
            setMapURLError(undefined);
        } else {
            // Reset form for create mode or when dialog is re-opened
            setSelectedBusinessPartnerId(allBusinessPartnerInfoList.length > 0 ? "" : ""); // Or first item: allBusinessPartnerInfoList[0].businessPartnerId
            setExpirationDateTime(dayjs().tz("Asia/Tokyo").add(1, 'day'));
            setRemarks("");
            setShowRoute(false);
            setShowIrregularFlights(false);
            setMapURL(undefined);
            setInfoMessage(undefined);
            setMapURLError(undefined);
        }
    }, [props.isCreateMapURLDialogOpen, props.isEditMode, props.mapInfoToEdit, allBusinessPartnerInfoList]);


    /**
     * パートナーIDが変更された時
     * @param event 
     */
    const handlePartnerIdChange = (event: SelectChangeEvent<string>) => {
        setSelectedBusinessPartnerId(event.target.value ? event.target.value as string : "");
    };

    /**
     * 有効期限が変更された時
     * @param event 
     */
    const handleExpirationDateTimeChange = (newValue: Dayjs | null) => {
        if (newValue) {
            setExpirationDateTime(newValue);
        };
    };

    /**
     * 備考が変更された時
     * @param event 
     */
    const handleRemarksChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        setRemarks(event.target.value);
    };

    /**
     * 「ルートを表示するか」チェックボックスが変更された時
     * @param event 
     */
    const handleShowRouteChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setShowRoute(event.target.checked);
    };

    /**
     * 「未追跡フライトを表示するか」チェックボックスが変更された時
     * @param event 
     */
    const handleShowUntrackedFlightsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setShowIrregularFlights(event.target.checked);
    };

    /**
     * MAPURL情報保存ボタンが押された時
     * @param event
     */
    const handleClickStoreMapURL = async () => {
        setMapURLError(undefined);
        setInfoMessage(undefined);
        setDisplayLoadingMark(true);

        if (!selectedBusinessPartnerId) {
            setMapURLError("ビジネスパートナーを選択してください");
            setDisplayLoadingMark(false);
            return;
        }
        if (!expirationDateTime || dayjs().isSame(expirationDateTime) || dayjs().isAfter(expirationDateTime)) {
            setMapURLError("有効期限は未来の日付を指定してください");
            setDisplayLoadingMark(false);
            return;
        }

        // DayjsのFormatは初期化時に設定されたタイムゾーン、指定のない場合は現地タイムゾーンで動作
        const ISO8601ExpirationDateTime = expirationDateTime?.format('YYYY-MM-DDTHH:mm:ss.SSSZ');

        let c: APIConnector = APIConnector.instance;

        try {
            if (props.isEditMode && props.mapInfoToEdit) {
                // Update existing URL
                if (props.mapInfoToEdit.urlId && ISO8601ExpirationDateTime) {
                    let afterEditMapURLInfo = structuredClone(props.mapInfoToEdit);
                    afterEditMapURLInfo.expirationDateTime = ISO8601ExpirationDateTime;
                    afterEditMapURLInfo.showRoute = showRoute;
                    afterEditMapURLInfo.showIrregularFlights = showIrregularFlights;
                    afterEditMapURLInfo.remarks = remarks;

                    await c.updateMapURLInformation(
                        afterEditMapURLInfo
                    );
                    setInfoMessage("URLを更新しました");
                    if (props.onMapURLSaved) {
                        props.onMapURLSaved(true);
                    }
                    setMapURL(afterEditMapURLInfo.url);
                } else {
                    throw new Error("更新に必要な情報が不足しています。");
                }
            } else {
                // Create new URL
                if (selectedBusinessPartnerId && ISO8601ExpirationDateTime) {
                    const newMapURL = await c.putMapURLInformation(
                        selectedBusinessPartnerId,
                        ISO8601ExpirationDateTime,
                        showRoute,
                        showIrregularFlights,
                        remarks
                    );
                    setMapURL(newMapURL);
                    setInfoMessage("URLを作成しました");
                    if (props.onMapURLSaved) {
                        props.onMapURLSaved(true);
                    }
                } else {
                     throw new Error("作成に必要な情報が不足しています。");
                }
            }
        } catch (error) {
            console.log(error);
            if (axios.isAxiosError(error) && error.response?.status === 403) {
                setMapURLError("本ビジネスパートナーは選択できません");
            } else {
                setMapURLError(props.isEditMode ? "URLの更新に失敗しました" : "URLの作成に失敗しました");
            }
        } finally {
            setDisplayLoadingMark(false);
        }
    };

    /**
     * クリップボードにコピーする
     */
    const handleCopyToClipboard = async () => {
        if (mapURL) {
            await global.navigator.clipboard.writeText(mapURL);
            setSnackbarOpen(true);
        }
    };

    return (
        <Dialog
            open={props.isCreateMapURLDialogOpen}
            onClose={props.handleClickCreateMapURLDialogClose}
        >
            <DialogTitle id="form-dialog-title">{props.isEditMode ? "マップURL編集" : "マップURL作成"}</DialogTitle>
            <DialogContent sx={{ paddingBottom: 0, width: 300 }}>
                <Grid container spacing={2}>
                    <Grid item container direction="column" spacing={2}>
                        {
                        infoMessage && mapURL ? // Show info message for update success
                            <Grid item xs>
                                <Alert
                                    severity="info"
                                    action={
                                        <Tooltip title="URLをコピー">
                                            <IconButton
                                                aria-label="copy"
                                                color="inherit"
                                                size="small"
                                                onClick={handleCopyToClipboard}
                                            >
                                                <ContentCopyIcon fontSize="inherit" />
                                            </IconButton>
                                        </Tooltip>
                                    }
                                >{infoMessage}</Alert>
                            </Grid>
                            : null
                        }
                        {mapURLError ?
                            <Grid item xs>
                                <Alert
                                    severity="error"
                                >{mapURLError}</Alert>
                            </Grid>
                            : undefined
                        }
                        {businessPartnerInfoError ?
                            <Grid item xs>
                                <Alert
                                    severity="error"
                                >{businessPartnerInfoError}</Alert>
                            </Grid>
                            : undefined
                        }
                        <Grid item xs textAlign={"center"}>
                            <FormControl sx={{ width: 250, marginTop: 1 }} fullWidth>
                                <InputLabel
                                    id="partner-id-select-label"
                                    required>パートナーID</InputLabel>
                                <Select
                                    sx={{ textAlign: "left" }}
                                    labelId="partner-id-select-label"
                                    label="パートナーID"
                                    id="partner-id-select"
                                    value={selectedBusinessPartnerId}
                                    onChange={handlePartnerIdChange}
                                    required
                                    disabled={props.isEditMode} // Disable in edit mode
                                >
                                    {allBusinessPartnerInfoList.map((businessPartnerInfo, i) => (
                                        <MenuItem
                                            key={i}
                                            value={businessPartnerInfo.businessPartnerId}>
                                            {businessPartnerInfo.businessPartnerId}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs textAlign={"center"}>
                            <FormControl sx={{ width: 250, marginTop: 1 }} fullWidth>
                                <LocalizationProvider
                                    dateAdapter={AdapterDayjs}>
                                    {/*
                                        inputformatに日本語を使用すると
                                        直接入力時に値が正しく入らない
                                    */}
                                    <DateTimePicker
                                        label="有効期限"
                                        format="YYYY/MM/DD HH:mm"
                                        value={expirationDateTime}
                                        onChange={handleExpirationDateTimeChange}
                                        disablePast
                                    />
                                </LocalizationProvider>
                            </FormControl>
                        </Grid>
                        <Grid item xs sx={{ paddingLeft: 2, textAlign: 'left', marginTop: 1 }}>
                            <InputLabel>表示オプション</InputLabel>
                        </Grid>
                        <Grid item xs textAlign={"left"}>
                            <FormControlLabel
                                control={<Switch checked={showRoute} onChange={handleShowRouteChange} />}
                                label="ルート"
                                sx={{ marginLeft: 1, wordBreak: "keep-all" }}
                            />
                        </Grid>
                        <Grid item xs textAlign={"left"}>
                            <FormControlLabel
                                control={<Switch checked={showIrregularFlights} onChange={handleShowUntrackedFlightsChange} />}
                                label="未追跡フライト"
                                sx={{ marginLeft: 1, wordBreak: "keep-all" }}
                            />
                        </Grid>
                        <Grid item xs textAlign={"center"}>
                            <FormControl sx={{ width: 250, marginTop: 1 }} fullWidth>
                                <TextField
                                    id="remarks-text-field"
                                    label="備考"
                                    multiline
                                    rows={2}
                                    value={remarks}
                                    onChange={event => handleRemarksChange(event)}
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ margin: "16px" }}>
                <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    startIcon={<LinkIcon />}
                    onClick={handleClickStoreMapURL} // Changed from () => handleClickCreateMapURL()
                >
                    {props.isEditMode ? "URL更新" : "URL作成"}
                </Button>
            </DialogActions>
            <SixSecondsDisplaySnackbar
                message={"URLをコピーしました"}
                snackbarOpen={snackbarOpen}
                setSnackbarOpen={setSnackbarOpen}
            />
            <Backdrop
                sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: '#fff' }}
                open={isDisplayLoadingMark}>
                <CircularProgress color="inherit" />
            </Backdrop>
        </Dialog>
    )
}